import React, { useState, useEffect } from 'react';
import { Amount } from '@redpanda/ui';
import { getSalesFunnelStats } from '../services/api';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { SALES_FUNNEL_CACHE_KEY as CACHE_KEY, useDataChanged } from '../utils/dataEvents';
import { toIntlLocale, useRememberedState } from '@redpanda/utils';

interface StageData {
  stage: {
    id: string;
    name: string;
    orderIndex: number;
    probability: number;
  };
  count: number;
  totalValue: number;
  averageValue: number;
}

interface CacheData {
  data: StageData[];
  timestamp: number;
}

const VALUE_MODES = ['count', 'value'] as const;

// 快取設定（key 定義在 utils/dataEvents.ts，商機異動時由 notifyDataChanged 清除）
const CACHE_DURATION = 5 * 60 * 1000; // 5分鐘

const getCachedData = (): StageData[] | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const cacheData: CacheData = JSON.parse(cached);
    return Date.now() - cacheData.timestamp < CACHE_DURATION ? cacheData.data : null;
  } catch (error) {
    console.warn('Failed to read sales funnel cache:', error);
    return null;
  }
};

const setCachedData = (data: StageData[]) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch (error) {
    console.warn('Failed to cache sales funnel data:', error);
  }
};

/**
 * 銷售漏斗（docs/介面優化.md §3-6）
 * 各階段一列：比例條（主色）＋數量或金額＋到下一階段的轉換率；不用彩虹色、不畫圓餅
 * 轉換率用「到達此階段的累計數」計算：到達(i) = 第 i 階段之後（含）所有商機數，
 * 所以永遠 ≤ 100%（原本拿「目前停在各階段的數量」相除，會出現 106%、116%）
 */
const SalesFunnelChart: React.FC = () => {
  const { t, i18n } = useTranslation();
  // 只在第一次 render 讀快取（不要每次 render 都 JSON.parse localStorage）
  const [initialCachedData] = useState(getCachedData);
  const [data, setData] = useState<StageData[]>(initialCachedData || []);
  const [loading, setLoading] = useState(!initialCachedData);
  const [valueMode, setValueMode] = useRememberedState<'count' | 'value'>('crm-ui:funnel.valueMode', 'count', VALUE_MODES);

  // 商機或階段異動後重新載入，畫面上的漏斗不會停在舊數字
  useDataChanged('opportunities', () => loadFunnelData(true));

  const loadFunnelData = async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getCachedData();
      if (cached) {
        setData(cached);
        setLoading(false);
        return;
      }
    }
    try {
      if (data.length === 0) setLoading(true);
      const response = await getSalesFunnelStats();
      if (Array.isArray(response.data)) {
        setData(response.data);
        setCachedData(response.data);
      }
    } catch (error) {
      console.error('Failed to load sales funnel data:', error);
      toast.error(t('components.salesFunnel.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialCachedData) loadFunnelData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-line p-5 text-sm text-fg-secondary">
        {t('components.salesFunnel.loading')}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-surface rounded-lg border border-line p-5 text-sm text-fg-secondary">
        {t('components.salesFunnel.noData')}
        <button onClick={() => loadFunnelData(true)} className="ml-3 text-accent hover:underline">
          {t('components.salesFunnel.reload')}
        </button>
      </div>
    );
  }

  const stages = [...data].sort((a, b) => a.stage.orderIndex - b.stage.orderIndex);
  // 機率 0 的階段（輸單）不算在漏斗的轉換裡
  const pipeline = stages.filter(s => s.stage.probability > 0);
  const reached = (stageId: string) => {
    const i = pipeline.findIndex(s => s.stage.id === stageId);
    return i < 0 ? 0 : pipeline.slice(i).reduce((n, s) => n + s.count, 0);
  };
  const metric = (s: StageData) => (valueMode === 'count' ? s.count : s.totalValue);
  const max = Math.max(...stages.map(metric), 1);

  const totalCount = stages.reduce((n, s) => n + s.count, 0);
  const totalValue = stages.reduce((n, s) => n + s.totalValue, 0);
  const locale = toIntlLocale(i18n.language);

  return (
    <section className="bg-surface rounded-lg border border-line p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-fg">{t('components.salesFunnel.title')}</h2>
        <div className="flex items-center gap-2">
          {/* 數量／金額切換（分段按鈕） */}
          <div className="inline-flex rounded-md border border-line p-0.5" role="group">
            {VALUE_MODES.map(mode => (
              <button
                key={mode}
                type="button"
                aria-pressed={valueMode === mode}
                onClick={() => setValueMode(mode)}
                className={`px-3 py-1 text-[13px] font-medium rounded transition-colors ${
                  valueMode === mode ? 'bg-slate-100 dark:bg-slate-800 text-fg' : 'text-fg-secondary hover:text-fg'
                }`}
              >
                {t(`components.salesFunnel.valueModes.${mode}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => loadFunnelData(true)}
            className="px-3 py-1 text-[13px] font-medium text-fg-secondary hover:text-fg transition-colors"
          >
            {t('components.salesFunnel.reload')}
          </button>
        </div>
      </div>

      <ul className="space-y-2.5">
        {stages.map((s, index) => {
          const next = pipeline[pipeline.findIndex(p => p.stage.id === s.stage.id) + 1];
          const from = reached(s.stage.id);
          const rate = next && from > 0 ? Math.round((reached(next.stage.id) / from) * 100) : null;
          return (
            <li key={s.stage.id} className="grid grid-cols-[7rem_1fr_7rem_6rem] items-center gap-3 text-sm">
              <span className="truncate text-fg">{s.stage.name}</span>
              <div className="h-5 rounded bg-slate-100 dark:bg-slate-800" aria-hidden="true">
                <div className="h-5 rounded bg-accent" style={{ width: `${(metric(s) / max) * 100}%`, minWidth: metric(s) > 0 ? 4 : 0 }} />
              </div>
              <span className="text-right tabular-nums text-fg">
                {valueMode === 'count' ? s.count.toLocaleString(locale) : <Amount value={s.totalValue} />}
              </span>
              <span className="text-right text-[13px] tabular-nums text-fg-secondary">
                {rate !== null && index < stages.length - 1 ? `→ ${rate}%` : ''}
              </span>
            </li>
          );
        })}
      </ul>

      {/* 摘要一行，數字不上色（§3-2） */}
      <p className="mt-4 pt-3 border-t border-line text-[13px] text-fg-secondary tabular-nums">
        {t('components.salesFunnel.labels.totalOpportunities')} <span className="text-fg">{totalCount.toLocaleString(locale)}</span>
        <span className="mx-2 text-fg-muted">·</span>
        {t('components.salesFunnel.labels.totalAmount')} <span className="text-fg"><Amount value={totalValue} /></span>
        <span className="mx-2 text-fg-muted">·</span>
        {t('components.salesFunnel.labels.averageAmount')} <span className="text-fg"><Amount value={totalCount ? Math.round(totalValue / totalCount) : 0} /></span>
        <span className="ml-3 text-fg-muted">{t('components.salesFunnel.conversionHint')}</span>
      </p>
    </section>
  );
};

export default SalesFunnelChart;
